import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import {
  Briefcase,
  Clock,
  Calendar,
  Check,
  X,
  AlertTriangle,
} from 'lucide-react';

type AdvanceTab = 'request' | 'history' | 'installments';

export function EmployeeSalaryAdvance() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<AdvanceTab>('request');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [amount, setAmount] = useState('');

  // Fetch availability
  const { data: availabilityData, isLoading: availabilityLoading } = useQuery({
    queryKey: ['employee', 'salary-advance', 'availability'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/salary-advance/availability');
      return response.data;
    },
  });

  // Fetch advance history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['employee', 'salary-advance', 'history'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/salary-advance/history');
      return response.data;
    },
    enabled: activeTab === 'history',
  });

  // Fetch pending installments
  const { data: installmentsData, isLoading: installmentsLoading } = useQuery({
    queryKey: ['employee', 'salary-advance', 'installments'],
    queryFn: async () => {
      const response = await api.get('/banking/employee/salary-advance/installments');
      return response.data;
    },
    enabled: activeTab === 'installments',
  });


  // Request advance mutation
  const requestAdvanceMutation = useMutation({
    mutationFn: async (data: { amount: number }) => {
      const response = await api.post('/banking/employee/salary-advance/request', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', 'salary-advance'] });
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      setShowConfirmModal(false);
      setAmount('');
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const parseAmount = (value: string): number => {
    const cleaned = value.replace(/\D/g, '');
    return parseInt(cleaned, 10) || 0;
  };

  const handleAmountChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const cents = parseInt(numericValue, 10) || 0;
    const maxAmount = availabilityData?.availableAmount || 0;
    const limitedCents = Math.min(cents, maxAmount);
    const formatted = (limitedCents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setAmount(formatted);
  };

  const handleRequestAdvance = () => {
    const amountCents = parseAmount(amount);
    requestAdvanceMutation.mutate({ amount: amountCents });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': case 'CREDITED': return 'bg-green-500/20 text-green-400';
      case 'PENDING': return 'bg-yellow-500/20 text-yellow-400';
      case 'REJECTED': return 'bg-red-500/20 text-red-400';
      default: return 'bg-surface-500/20 text-surface-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'Aprovado';
      case 'CREDITED': return 'Creditado';
      case 'PENDING': return 'Pendente';
      case 'REJECTED': return 'Rejeitado';
      default: return status;
    }
  };

  const tabs = [
    { id: 'request' as AdvanceTab, label: 'Solicitar', icon: <Briefcase size={18} /> },
    { id: 'history' as AdvanceTab, label: 'Histórico', icon: <Clock size={18} /> },
    { id: 'installments' as AdvanceTab, label: 'Parcelas', icon: <Calendar size={18} /> },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Adiantamento Salarial</h1>
        <p className="text-surface-400">Solicite adiantamento do seu salário</p>
      </div>

      {/* Availability Card */}
      <Card className="bg-gradient-to-br from-accent-600 to-accent-800 border-0">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-accent-200">Disponível para adiantamento</span>
            <span className="text-accent-200 text-sm">
              {availabilityData?.percentageAvailable || 0}% do salário
            </span>
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            {availabilityLoading ? '-' : formatCurrency(availabilityData?.availableAmount || 0)}
          </p>
          <p className="text-sm text-accent-200">
            Salário base: {formatCurrency(availabilityData?.baseSalary || 0)}
          </p>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Request Tab */}
      {activeTab === 'request' && (
        <Card>
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white">Solicitar Adiantamento</h2>

            {availabilityData?.availableAmount > 0 ? (
              <>
                <div>
                  <Input
                    label="Valor do adiantamento"
                    placeholder="0,00"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    leftIcon={<span className="text-surface-400">R$</span>}
                  />
                  <p className="text-xs text-surface-400 mt-2">
                    Máximo disponível: {formatCurrency(availabilityData?.availableAmount || 0)}
                  </p>
                </div>

                {/* Quick Amount Buttons */}
                <div className="flex flex-wrap gap-2">
                  {[25, 50, 75, 100].map((percent) => {
                    const value = Math.floor((availabilityData?.availableAmount || 0) * percent / 100);
                    return (
                      <button
                        key={percent}
                        onClick={() => {
                          const formatted = (value / 100).toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          });
                          setAmount(formatted);
                        }}
                        className="px-3 py-1.5 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 text-sm"
                      >
                        {percent}% ({formatCurrency(value)})
                      </button>
                    );
                  })}
                </div>

                {/* Info Box */}
                <div className="p-4 rounded-lg bg-surface-800/50 border border-surface-700">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-yellow-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-white font-medium">Informações importantes</p>
                      <ul className="text-xs text-surface-400 mt-2 space-y-1">
                        <li>• O valor será descontado no próximo pagamento</li>
                        <li>• Taxa de {availabilityData?.feePercentage || 0}% sobre o valor</li>
                        <li>• Aprovação sujeita a análise</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={!amount || parseAmount(amount) === 0}
                >
                  Solicitar Adiantamento
                </Button>
              </>
            ) : (
              <div className="text-center py-8">
                <AlertTriangle size={48} className="mx-auto text-yellow-400 mb-3" />
                <p className="text-white font-medium">Adiantamento não disponível</p>
                <p className="text-sm text-surface-400 mt-2">
                  {availabilityData?.reason || 'Você já utilizou o limite disponível para este período.'}
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Histórico de Adiantamentos</h2>

            {historyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-5 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : historyData?.advances?.length > 0 ? (
              <div className="space-y-3">
                {historyData.advances.map((advance: any) => (
                  <div
                    key={advance.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-surface-800/50"
                  >
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${advance.status === 'CREDITED' ? 'bg-green-500/20' : 
                        advance.status === 'PENDING' ? 'bg-yellow-500/20' : 'bg-red-500/20'}
                    `}>
                      {advance.status === 'CREDITED' ? <Check size={20} className="text-green-400" /> :
                       advance.status === 'PENDING' ? <Clock size={20} className="text-yellow-400" /> :
                       <X size={20} className="text-red-400" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">
                        Adiantamento de {formatCurrency(advance.amount)}
                      </p>
                      <p className="text-xs text-surface-400">
                        Solicitado em {formatDate(advance.requestedAt)}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(advance.status)}`}>
                      {getStatusLabel(advance.status)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Briefcase size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum adiantamento solicitado</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Installments Tab */}
      {activeTab === 'installments' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Parcelas Pendentes</h2>

            {installmentsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                    <div className="h-3 w-48 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : installmentsData?.installments?.length > 0 ? (
              <div className="space-y-3">
                {installmentsData.installments.map((installment: any) => (
                  <div
                    key={installment.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50"
                  >
                    <div>
                      <p className="text-sm font-medium text-white">
                        Parcela {installment.number}/{installment.total}
                      </p>
                      <p className="text-xs text-surface-400">
                        Desconto em {formatDate(installment.dueDate)}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-white">
                      {formatCurrency(installment.amount)}
                    </span>
                  </div>
                ))}

                <div className="pt-4 border-t border-surface-800">
                  <div className="flex justify-between">
                    <span className="text-surface-400">Total pendente</span>
                    <span className="text-lg font-semibold text-white">
                      {formatCurrency(installmentsData.totalPending)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Check size={48} className="mx-auto text-green-400 mb-3" />
                <p className="text-green-400 font-medium">Tudo em dia!</p>
                <p className="text-sm text-surface-400 mt-1">
                  Nenhuma parcela pendente
                </p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Confirm Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Adiantamento"
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-surface-800">
            <div className="flex justify-between mb-2">
              <span className="text-surface-400">Valor solicitado</span>
              <span className="text-white font-semibold">R$ {amount}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-surface-400">Taxa ({availabilityData?.feePercentage || 0}%)</span>
              <span className="text-red-400">
                -{formatCurrency(parseAmount(amount) * (availabilityData?.feePercentage || 0) / 100)}
              </span>
            </div>
            <div className="flex justify-between pt-2 border-t border-surface-700">
              <span className="text-surface-400">Valor líquido</span>
              <span className="text-white font-semibold">
                {formatCurrency(parseAmount(amount) * (1 - (availabilityData?.feePercentage || 0) / 100))}
              </span>
            </div>
          </div>

          <p className="text-xs text-surface-400">
            O valor será creditado em sua conta após aprovação e descontado no próximo pagamento.
          </p>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleRequestAdvance}
              disabled={requestAdvanceMutation.isPending}
            >
              {requestAdvanceMutation.isPending ? 'Solicitando...' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default EmployeeSalaryAdvance;
